import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  Cell,
  type CellContext,
  Header,
  type HeaderContext,
  type Row,
  type Table,
  flexRender,
} from '@tanstack/react-table'
import { debounce } from 'es-toolkit'
import { IconType } from 'react-icons'
import {
  HiMiniChevronDown,
  HiMiniChevronLeft,
  HiMiniChevronRight,
  HiMiniChevronUp,
  HiMiniChevronUpDown,
  HiMiniPlus,
  HiMiniXMark,
  HiOutlineArrowPath,
  HiOutlineTrash,
  HiXMark,
} from 'react-icons/hi2'
import { LuMerge, LuSearch } from 'react-icons/lu'
import { twMerge } from 'tailwind-merge'

import { MiniBadge } from '@/components/common/Badge'
import { Badge } from '@/components/common/BadgeV2'
import Button from '@/components/common/Button'
import Checkbox from '@/components/common/Checkbox'
import {
  Combobox,
  ComboboxTrigger,
} from '@/components/common/ComboboxV2/Combobox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/common/DropdownMenu'
import PopoverV2 from '@/components/common/PopoverV2'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/common/Select'
import Spinner from '@/components/common/Spinner'
import { Input, SearchInput } from '@/components/common/TextInputV2'
import { TagComboboxContent } from '@/components/tags/TagCombobox'
import { TagDropdownMenuSubContent } from '@/components/tags/TagDropdownMenuSubContent'

import type { Variant } from '@/types/components/Variant'
import type { UUID } from '@/types/Id'
import type { TagType } from '@/types/Tag'

export interface TablePageHeaderProps {
  title: string
  description?: string
  count?: number
  query?: string
  className?: string
  onQueryChange?: (query: string) => void
}

export const DataTablePageHeader: React.FC<TablePageHeaderProps> = ({
  title,
  description,
  count,
  query,
  className,
  onQueryChange,
}) => {
  const handleQueryChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onQueryChange?.(e.target.value)
    },
    [onQueryChange],
  )
  return (
    <div
      id="data-table-page-header"
      className={twMerge(
        'w-full max-w-full',
        'px-3 py-2 border-b border-primary-200 dark:border-primary-800',
        'sticky left-0 z-10',
        className,
      )}
      style={{
        position: '-webkit-sticky',
      }}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-medium text-primary-900 dark:text-primary-100">
              {title}
            </h1>
            <p className="text-sm text-primary-600 dark:text-primary-400">
              {description}
              {count && ` • ${count} found`}
            </p>
          </div>
        </div>
        <div className="relative">
          <LuSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-primary-500 dark:text-primary-400" />
          <Input
            type="text"
            placeholder="Filter by name..."
            className="w-72 lg:w-96 h-8 pl-10 pr-4 text-sm border-primary-300 dark:border-primary-700 rounded-[5px]"
            value={query}
            onChange={handleQueryChange}
          />
        </div>
      </div>
    </div>
  )
}

interface TableSearchInputProps {
  query: string
  onQueryChange: (query: string) => void
  className?: string
}

export const DataTableSearchInput: React.FC<TableSearchInputProps> = ({
  query,
  onQueryChange,
  className,
}) => {
  const handleQueryChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onQueryChange?.(e.target.value)
    },
    [onQueryChange],
  )
  return (
    <div className="relative">
      <LuSearch
        size={14}
        className="absolute left-2 top-1/2 transform -translate-y-1/2 text-primary-500 dark:text-primary-400"
      />
      <Input
        type="text"
        placeholder="Search..."
        className={twMerge(
          'w-60 lg:w-[240px] h-7 pl-[30px] pr-3 text-sm border-primary-300 dark:border-primary-700 rounded-[5px]',
          className,
        )}
        value={query}
        onChange={handleQueryChange}
      />
    </div>
  )
}

interface DebouncedTableSearchInputProps {
  query: string
  onQueryChange: (query: string) => void
  debounceMs?: number
  className?: string
  containerClassName?: string
  isLoading?: boolean
}

/**
 * Debounced search input component for server-side search.
 *
 * Uses local buffered state for immediate UI updates while debouncing
 * the actual store updates to prevent spamming the server.
 *
 * @param query - The current search query value from the store
 * @param onQueryChange - Callback to update the store (debounced)
 * @param debounceMs - Debounce delay in milliseconds (default: 200)
 * @param className - Additional CSS classes to apply to the input
 * @param containerClassName - Additional CSS classes to apply to the container
 */
export const DebouncedDataTableSearchInput: React.FC<
  DebouncedTableSearchInputProps
> = ({
  query,
  onQueryChange,
  className,
  debounceMs = 200,
  containerClassName,
  isLoading = false,
}) => {
  // Local buffered state for immediate UI updates
  const [localQuery, setLocalQuery] = useState<string>(query)

  // Keep latest onQueryChange in a ref so the debounced fn always calls the latest
  const onChangeRef = useRef(onQueryChange)

  // Debounced function: emit only after the user pauses typing
  const debouncedOnChangeRef = useRef(
    debounce(
      (value: string) => {
        onChangeRef.current(value)
      },
      debounceMs,
      { edges: ['trailing'] },
    ),
  )

  const handleQueryChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      // Update local state immediately for responsive UI
      setLocalQuery(value)

      if (value === '') {
        // Clear immediately
        debouncedOnChangeRef.current.cancel()
        onChangeRef.current('')
      } else {
        // Schedule one trailing update after the user pauses typing
        debouncedOnChangeRef.current(value)
      }
    },
    [debounceMs],
  )

  // Sync local state when prop changes (e.g., from external clear)
  useEffect(() => {
    if (query !== localQuery) {
      setLocalQuery(query)
    }
  }, [query])

  useEffect(() => {
    onChangeRef.current = onQueryChange
  }, [onQueryChange])

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedOnChangeRef.current.cancel()
    }
  }, [])

  return (
    <div className={twMerge('relative', containerClassName)}>
      <LuSearch
        size={14}
        className="absolute left-2 top-1/2 transform -translate-y-1/2 text-primary-500 dark:text-primary-400"
      />
      <Input
        type="text"
        placeholder="Search..."
        className={twMerge(
          'w-60 lg:w-[260px] h-7 px-[30px] pr-3 text-sm border-primary-300 dark:border-primary-700 rounded-[5px]',
          className,
        )}
        value={localQuery}
        onChange={handleQueryChange}
      />
      {isLoading && (
        <Spinner
          variant="primary"
          className="absolute right-2 top-1.5 w-4 h-4"
        />
      )}
    </div>
  )
}

interface DataTableSearchPopoverProps {
  query: string
  onQueryChange: (query: string) => void
  hasSelection: boolean
  className?: string
  inputClassName?: string
}

/**
 * A search component that shows a full input when no rows are selected,
 * and collapses to a popover trigger when rows are selected.
 * This allows users to still search while performing bulk actions.
 */
export const DataTableSearchPopover: React.FC<DataTableSearchPopoverProps> = ({
  query,
  onQueryChange,
  hasSelection,
  className,
  inputClassName,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleQueryChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onQueryChange?.(e.target.value)
    },
    [onQueryChange],
  )

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open)
    if (open) {
      // Focus input when popover opens
      setTimeout(() => {
        inputRef.current?.focus()
      }, 50)
    }
  }, [])

  // When no selection, show regular search input
  if (!hasSelection) {
    return (
      <div className={twMerge('relative', className)}>
        <LuSearch
          size={14}
          className="absolute left-2 top-1/2 transform -translate-y-1/2 text-primary-500 dark:text-primary-400"
        />
        <Input
          type="text"
          placeholder="Search..."
          className={twMerge(
            'w-60 lg:w-[240px] h-7 pl-[30px] pr-3 text-sm border-primary-300 dark:border-primary-700 rounded-[5px]',
            inputClassName,
          )}
          value={query}
          onChange={handleQueryChange}
        />
      </div>
    )
  }

  // When has selection, show popover with search
  return (
    <PopoverV2 open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverV2.Trigger asChild>
        <Button variant="soft-compact" className="relative w-7 h-7">
          <div className="relative flex items-center justify-center">
            <LuSearch size={16} className="flex-shrink-0" />
            {query && query.length > 0 && (
              <div className="absolute -top-2 -right-2 rounded-full w-2.5 h-2.5 ring-2 ring-white dark:ring-primary-900 bg-red-600 dark:bg-red-500" />
            )}
          </div>
        </Button>
      </PopoverV2.Trigger>
      <PopoverV2.Content align="end" className="w-64 p-2" sideOffset={4}>
        <SearchInput
          ref={inputRef}
          type="text"
          placeholder="Search..."
          value={query}
          onChange={handleQueryChange}
          className="w-full"
          autoComplete="off"
        />
      </PopoverV2.Content>
    </PopoverV2>
  )
}

// Compact (chat-panel) tables put search on its own full-width toolbar row,
// styled to match the files-explorer toolbar search (height, radius, tint,
// clear affordance) so every panel list reads the same.
export const DataTableSearchRow: React.FC<{
  query: string
  onQueryChange: (query: string) => void
  placeholder?: string
}> = ({ query, onQueryChange, placeholder = 'Search...' }) => {
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onQueryChange(event.target.value)
    },
    [onQueryChange],
  )
  const handleClear = useCallback(() => {
    onQueryChange('')
  }, [onQueryChange])

  return (
    <SearchInput
      value={query}
      onChange={handleChange}
      onClear={handleClear}
      placeholder={placeholder}
      className="h-[30px] rounded-[6px] border-primary-300/80 bg-primary-50/70 dark:border-primary-700 dark:bg-primary-900/50"
      containerClassName="w-full"
      iconSize={14}
    />
  )
}

const handleCloseAutoFocus = (e: Event) => {
  e.preventDefault()
}

export interface SelectedFilter {
  label: string
  value: string
}

export interface DataTableActiveFilterChipProps {
  value: string
  onClick: (value: string) => void
  className?: string
  children: React.ReactNode
}

export const DataTableActiveFilterChip: React.FC<
  DataTableActiveFilterChipProps
> = ({ children, value, onClick, className }) => {
  const handleClick = useCallback(() => {
    onClick(value)
  }, [onClick, value])
  return (
    <Badge
      key={value}
      onClick={handleClick}
      className={twMerge(
        'inline-flex items-center cursor-pointer h-6 px-2 text-xs',
        'hover:bg-primary-200/70 dark:hover:bg-primary-700/70',
        className,
      )}
      variant="outline"
    >
      {children}
      <HiXMark size={10} className="ml-1 stroke-[1.5px]" />
    </Badge>
  )
}

interface TableFilterBadgesProps {
  value: SelectedFilter[]
  onClick: (value: string) => void
  containerClassName?: string
  badgeClassName?: string
}

export const DataTableActiveFilterChips: React.FC<TableFilterBadgesProps> = ({
  value,
  onClick,
  containerClassName,
  badgeClassName,
}) => {
  return (
    <div
      className={twMerge(
        'flex items-center gap-2 flex-wrap',
        containerClassName,
      )}
    >
      {value.map((value, idx) => (
        <DataTableActiveFilterChip
          key={idx}
          value={value.value}
          onClick={onClick}
          className={twMerge(badgeClassName)}
        >
          {value.label}
        </DataTableActiveFilterChip>
      ))}
    </div>
  )
}

interface TableFilterBadgeContainerProps {
  children: React.ReactNode
}

export const DataTableActiveFiltersBar: React.FC<
  TableFilterBadgeContainerProps
> = ({ children }) => {
  return <div className="flex items-center gap-2 mt-3">{children}</div>
}

interface TableFilterClearButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  onClick: () => void
}

export const DataTableClearFiltersButton = forwardRef<
  HTMLButtonElement,
  TableFilterClearButtonProps
>(({ onClick, ...props }, ref) => {
  return (
    <div className="flex items-center justify-between">
      <button
        ref={ref}
        className="text-blue-600 dark:text-blue-400 font-medium text-sm hover:opacity-70"
        onClick={onClick}
        {...props}
      >
        Clear
      </button>
    </div>
  )
})

DataTableClearFiltersButton.displayName = 'DataTableClearFiltersButton'

interface TableFilterControlsProps {
  children: React.ReactNode
  className?: string
}

export const DataTableFilterControls: React.FC<TableFilterControlsProps> = ({
  children,
  className,
}) => {
  return (
    <div className={twMerge('flex items-center gap-[10px]', className)}>
      {children}
    </div>
  )
}

interface TableFilterHeaderProps {
  children?: React.ReactNode
  className?: string
}

export const DataTableFilterToolbar: React.FC<TableFilterHeaderProps> = ({
  children,
  className,
}) => {
  return (
    <div
      id="data-table-filter-toolbar"
      className={twMerge(
        'sticky -top-[1px] left-0 z-10 bg-white dark:bg-default-dark',
        'overflow-x-auto',
        children
          ? 'p-3 border-b border-primary-200 dark:border-primary-800'
          : 'p-0',
        className,
      )}
      style={{
        position: '-webkit-sticky',
      }}
    >
      {children}
    </div>
  )
}

interface TableFooterProps {
  filteredCount: number
  totalCount: number
  currentPage: number
  pageCount: number
  previousDisabled: boolean
  nextDisabled: boolean
  dataLabel?: string
  pluralize?: boolean
  className?: string
  onPageChange?: (page: number) => void // 1-based index
  pageSize?: number
  onPageSizeChange?: (pageSize: number) => void
  pageSizeOptions?: number[]
  disabled?: boolean
}

export const DataTableFooter: React.FC<TableFooterProps> = ({
  filteredCount,
  totalCount,
  currentPage,
  pageCount,
  previousDisabled,
  nextDisabled,
  dataLabel,
  pluralize = true,
  className,
  onPageChange,
  pageSize,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  disabled = false,
}) => {
  const pages = useMemo(() => {
    if (pageCount <= 7) {
      return Array.from({ length: pageCount }, (_, i) => i + 1)
    }
    const last = pageCount
    const first = 1
    // Always 7 slots for pageCount >= 8
    if (currentPage <= 2) {
      // Near start: 1 2 3 4 5 … N
      return [1, 2, 3, 4, 5, 'ellipsis', last]
    }
    if (currentPage === 3) {
      // Just past half of initial window: 1 … 3 4 5 … N
      return [first, 'ellipsis', 3, 4, 5, 'ellipsis', last]
    }
    if (currentPage >= last - 1) {
      // Near end: 1 … N-4 N-3 N-2 N-1 N
      return [first, 'ellipsis', last - 4, last - 3, last - 2, last - 1, last]
    }
    if (currentPage === last - 2) {
      // Just before the tail cluster: 1 … N-4 N-3 N-2 N-1 N
      return [first, 'ellipsis', last - 4, last - 3, last - 2, last - 1, last]
    }
    // Middle: 1 … c-1 c c+1 … N
    return [
      first,
      'ellipsis',
      currentPage - 1,
      currentPage,
      currentPage + 1,
      'ellipsis',
      last,
    ]
  }, [currentPage, pageCount])

  const onPrev = useCallback(() => {
    if (disabled || !onPageChange || currentPage <= 1) return
    onPageChange(currentPage - 1)
  }, [disabled, onPageChange, currentPage])

  const onNext = useCallback(() => {
    if (disabled || !onPageChange || currentPage >= pageCount) return
    onPageChange(currentPage + 1)
  }, [disabled, onPageChange, currentPage, pageCount])

  const handlePageClick = React.useCallback(
    (page: string | number) => (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled || !onPageChange) return
      const pageNumber = Number(page)
      if (!Number.isNaN(pageNumber)) onPageChange(pageNumber)
    },
    [disabled, onPageChange],
  )

  const handlePageSizeChange = useCallback(
    (value: string) => {
      if (disabled || !onPageSizeChange) return
      onPageSizeChange(Number(value))
    },
    [disabled, onPageSizeChange],
  )

  return (
    <div
      id="data-table-footer"
      className={twMerge(
        'bg-primary-100 america:bg-primary-50 dark:bg-primary-900 border-t border-primary-200 dark:border-primary-800 text-primary-600 dark:text-primary-400 px-3 py-2 flex items-center justify-between text-sm',
        'space-x-3',
        'w-full sticky left-0 bottom-0',
        className,
      )}
      style={{
        position: '-webkit-sticky',
      }}
    >
      <div className="flex items-center gap-3">
        {pageSize !== undefined && onPageSizeChange && (
          <>
            <div className="flex items-center gap-2">
              <span className="text-sm text-primary-500 dark:text-primary-400">
                Show:
              </span>
              <Select
                value={String(pageSize)}
                onValueChange={handlePageSizeChange}
                disabled={disabled}
              >
                <SelectTrigger className="h-[24px] w-16 pl-2 pr-1.5 text-[13px] rounded-[4px] bg-white dark:bg-default-dark">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pageSizeOptions.map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="h-5 w-px bg-primary-300 dark:bg-primary-800" />
          </>
        )}
        <div className="flex items-center gap-1">
          <Button
            variant="outline-compact"
            aria-label="Previous page"
            disabled={disabled || previousDisabled}
            onClick={onPrev}
            className="min-w-6 h-6 pl-1 pr-2 mr-0.5 rounded-[4px] transition-colors duration-200"
          >
            <HiMiniChevronLeft size={15} />
            <span className="text-[13px]">Previous</span>
          </Button>
          {pages.map((p, idx) =>
            p === 'ellipsis' ? (
              <span
                key={idx}
                className="min-w-6 h-6 inline-flex items-center justify-center rounded-[4px] text-xs text-primary-500 dark:text-primary-400"
              >
                …
              </span>
            ) : (
              <Button
                key={idx}
                variant={
                  p === currentPage ? 'primary-compact' : 'outline-compact'
                }
                aria-current={p === currentPage ? 'page' : undefined}
                className={twMerge(
                  'min-w-6 h-6 px-0 rounded-[4px] transition-colors duration-200',
                  p === currentPage
                    ? ''
                    : 'border-transparent bg-transparent hover:border-primary-300 dark:hover:border-primary-700 hover:bg-white dark:hover:bg-default-dark',
                )}
                data-page={p}
                disabled={disabled}
                onClick={handlePageClick(p)}
              >
                <span className="text-xs">{p}</span>
              </Button>
            ),
          )}
          <Button
            variant="outline-compact"
            aria-label="Next page"
            disabled={disabled || nextDisabled}
            onClick={onNext}
            className="min-w-6 h-6 pl-2 pr-1 ml-0.5 rounded-[4px] transition-colors duration-200"
          >
            <span className="text-[13px]">Next</span>
            <HiMiniChevronRight size={15} />
          </Button>
        </div>
      </div>
      <div className="flex items-center gap-3 truncate">
        <span className="text-sm text-primary-500 dark:text-primary-400 truncate">
          Showing <span className="font-semibold">{filteredCount}</span> of{' '}
          <span className="font-semibold">{totalCount}</span>{' '}
          {dataLabel ? dataLabel : 'item'}
          {pluralize && (totalCount === 1 ? '' : 's')}
        </span>
      </div>
    </div>
  )
}

export interface TableCellProps {
  cell: Cell<any, any>
  className?: string
  isFiltered?: boolean
  colSpan?: number
}

export const DataTableCell: React.FC<TableCellProps> = ({
  cell,
  className,
  isFiltered,
  colSpan,
}) => {
  const minSize = cell?.column.columnDef.minSize
  const maxSize = cell?.column.columnDef.maxSize
  return (
    <td
      className={twMerge(
        'align-middle h-full overflow-hidden',
        isFiltered ? 'bg-blue-100 dark:bg-blue-800/50' : '',
        className,
      )}
      style={{ minWidth: minSize, maxWidth: maxSize }}
      colSpan={colSpan}
    >
      {cell ? flexRender(cell.column.columnDef.cell, cell.getContext()) : null}
    </td>
  )
}

interface TableRowProps {
  children: React.ReactNode
  className?: string
}

export const DataTableRow: React.FC<TableRowProps> = ({
  children,
  className,
}) => {
  return (
    <tr
      className={twMerge(
        'group hover:bg-primary-50 dark:hover:bg-primary-900',
        className,
      )}
    >
      {children}
    </tr>
  )
}

interface TableSeparatorRowProps {
  label: string
  columnCount: number
  className?: string
  id?: string
}

export const DataTableSeparatorRow: React.FC<TableSeparatorRowProps> = ({
  label,
  columnCount,
  className,
  id,
}) => {
  return (
    <tr
      id={id}
      className={twMerge(
        'bg-transparent first:border-t border-primary-200 dark:border-primary-800',
        className,
      )}
    >
      <td colSpan={columnCount} className="p-0 relative">
        <div className="flex items-center w-full px-[19px] py-3 bg-primary-50 dark:bg-primary-900">
          <span className="text-xs font-semibold text-primary-700 dark:text-primary-300 uppercase tracking-wider pl-[42px]">
            {label}
          </span>
        </div>
      </td>
    </tr>
  )
}

export const DataTableLoadingOverlay: React.FC = () => {
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 dark:bg-primary-900/50 pointer-events-none">
      <Spinner />
    </div>
  )
}

export interface TableHeaderCellProps {
  header: Header<any, any>
  isFiltered?: boolean
  className?: string
}

interface SortButtonProps {
  isSorted: 'asc' | 'desc' | false
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
}

export const DataTableSortButton: React.FC<SortButtonProps> = ({
  isSorted,
  onClick,
}) => {
  return (
    <button className="inline-flex items-center" onClick={onClick}>
      {isSorted === 'asc' ? (
        <HiMiniChevronUp
          size={16}
          className="flex-shrink-0 text-primary-500 dark:text-primary-400"
        />
      ) : isSorted === 'desc' ? (
        <HiMiniChevronDown
          size={16}
          className="flex-shrink-0 text-primary-500 dark:text-primary-400"
        />
      ) : (
        <HiMiniChevronUpDown
          size={16}
          className="flex-shrink-0 text-primary-500 dark:text-primary-400"
        />
      )}
    </button>
  )
}

export const DataTableHeadCell: React.FC<TableHeaderCellProps> = ({
  header,
  isFiltered,
  className,
}) => {
  const minSize = header?.column.columnDef.minSize
  const maxSize = header?.column.columnDef.maxSize
  const size = header?.column.columnDef.size
  const metaHeaderClassName = (
    header?.column.columnDef.meta as { headerClassName?: string } | undefined
  )?.headerClassName
  const canSort = header?.column.getCanSort?.()
  const isSorted = header?.column.getIsSorted?.()
  const toggleSorting = header?.column.getToggleSortingHandler?.()

  const renderedCell = header
    ? flexRender(header.column.columnDef.header, header.getContext())
    : null
  return (
    <th
      className={twMerge(
        'font-medium text-primary-800 py-2 text-left',
        'bg-primary-100 america:bg-primary-50 dark:bg-primary-900',
        isFiltered ? 'bg-blue-200/80 dark:bg-blue-700/50' : '',
        metaHeaderClassName,
        className,
      )}
      style={{ minWidth: minSize, maxWidth: maxSize, width: size }}
    >
      {header?.isPlaceholder ? null : canSort ? (
        <span className="flex items-center gap-2">
          {renderedCell}
          {canSort && (
            <DataTableSortButton isSorted={isSorted} onClick={toggleSorting} />
          )}
        </span>
      ) : (
        renderedCell
      )}
    </th>
  )
}

interface DataTableHeaderCellContentProps {
  children: React.ReactNode
  className?: string
}

export const DataTableHeaderCellContent: React.FC<
  DataTableHeaderCellContentProps
> = ({ children, className }) => {
  return (
    <div
      className={twMerge(
        'font-medium text-sm text-primary-700 dark:text-primary-300 whitespace-nowrap',
        className,
      )}
    >
      {children}
    </div>
  )
}

interface TableHeaderProps {
  children: React.ReactNode
  className?: string
}

export const DataTableHead: React.FC<TableHeaderProps> = ({
  children,
  className,
}) => {
  return (
    <thead
      className={twMerge('sticky z-[5] top-0', className)}
      style={{
        position: '-webkit-sticky',
      }}
    >
      <tr
        className={twMerge(
          'bg-primary-100 america:bg-primary-50 dark:bg-primary-900',
          "after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-[100%] after:border-b after:border-primary-200 after:dark:border-primary-800",
          className,
        )}
      >
        {children}
      </tr>
    </thead>
  )
}

interface TableBodyProps {
  className?: string
  children: React.ReactNode
}

export const DataTableBody: React.FC<TableBodyProps> = ({
  children,
  className,
}) => {
  return (
    <tbody
      className={twMerge(
        'h-full w-full divide-y divide-primary-200 dark:divide-primary-800',
        className,
      )}
    >
      {children}
    </tbody>
  )
}

interface TableProps {
  className?: string
  children: React.ReactNode
  /**
   * Constrain the table to its container width. The default `min-w-full`
   * leaves `width: auto`, letting content push the table wider than the
   * viewport (horizontal scrolling). `fit` pins the table to exactly 100%
   * width with fixed layout; pair it with one header cell carrying
   * `w-full` (the flexible column) so every other column keeps its
   * explicit pixel width and the flexible one absorbs the remainder.
   */
  fit?: boolean
}

export const DataTable: React.FC<TableProps> = ({
  children,
  className,
  fit = false,
}) => {
  return (
    <div id="data-table" className={twMerge('w-full', className)}>
      <table className={fit ? 'w-full table-fixed' : 'min-w-full table-fixed'}>
        {children}
      </table>
    </div>
  )
}

interface DataTableEmptyProps {
  children: React.ReactNode
  className?: string
}

export const DataTableEmpty: React.FC<DataTableEmptyProps> = ({
  children,
  className,
}) => {
  return (
    <div
      className={twMerge(
        'flex items-center justify-center min-h-full',
        className,
      )}
    >
      {children}
    </div>
  )
}

interface DataTableDefaultEmptyProps {
  title: string
  description?: string
  className?: string
  children?: React.ReactNode
  onClearSearch?: () => void
  onClearFilters?: () => void
  icon?: IconType
}

export const DataTableDefaultEmpty: React.FC<DataTableDefaultEmptyProps> = ({
  onClearSearch,
  onClearFilters,
  icon,
  title,
  description,
  className,
  children,
}) => {
  const Icon = icon
  return (
    <DataTableEmpty>
      <div
        className={twMerge('text-center max-w-md mx-auto space-y-4', className)}
      >
        <div>
          {Icon && (
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary-100 dark:bg-primary-800 flex items-center justify-center">
              <Icon size={20} />
            </div>
          )}
          <h3 className="text-md font-medium text-primary-900 dark:text-primary-100 mb-2">
            {title}
          </h3>
          {children ? (
            children
          ) : description ? (
            <>
              <p className="text-sm text-primary-500 dark:text-primary-400">
                {description}
              </p>
            </>
          ) : null}
        </div>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {onClearFilters && (
              <Button onClick={onClearFilters} variant="outline-compact">
                <HiOutlineArrowPath size={16} />
                <span>Reset Filters</span>
              </Button>
            )}
            {onClearSearch && (
              <Button onClick={onClearSearch} variant="blue-compact">
                <HiMiniXMark size={16} />
                <span>Clear Search</span>
              </Button>
            )}
          </div>
        </div>
      </div>
    </DataTableEmpty>
  )
}

interface DataTableNoEntriesProps {
  icon: IconType
  title: string
  description: string | React.ReactNode
  children?: React.ReactNode
}

export const DataTableNoEntries: React.FC<DataTableNoEntriesProps> = ({
  icon: Icon,
  title,
  description,
  children,
}) => {
  return (
    <div className="flex flex-col items-center justify-center w-full h-full max-w-[500px] mx-auto space-y-4 py-12">
      <div className="flex items-center justify-center w-14 h-14 bg-blue-50 dark:bg-blue-900/20 rounded-full">
        <Icon size={24} className="text-blue-600 dark:text-blue-400" />
      </div>
      <div className="text-center space-y-2">
        <h3 className="text-lg font-semibold text-primary-900 dark:text-primary-100">
          {title}
        </h3>
        <p className="text-sm text-primary-600 dark:text-primary-400 leading-relaxed">
          {description}
        </p>
      </div>
      {children}
    </div>
  )
}

interface BulkTagComboboxProps extends Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  'onChange' | 'children'
> {
  open: boolean
  onOpenChange: (open: boolean) => void
  // `null` means an explicit clear-all request. An empty array means no tags
  // are currently staged to add.
  onChange: (value: string[] | null) => Promise<void> | void
  label?: string
  disabled?: boolean
  tagType?: TagType
}

const emptyTagIds: string[] = []

export const BulkTagCombobox = forwardRef<
  HTMLSpanElement,
  BulkTagComboboxProps
>(
  (
    { open, onOpenChange, onChange, label = 'Tag', disabled, tagType, ...rest },
    ref,
  ) => {
    const [selectedTagIds, setSelectedTagIds] = useState<string[] | null>(null)
    const [isClearSelected, setIsClearSelected] = useState(false)
    const [isSaving, setIsSaving] = useState(false)

    const handleSelectionChange = useCallback(
      (value: string[] | null) => {
        if (isSaving) {
          return
        }

        setSelectedTagIds(value ?? [])
        setIsClearSelected(false)
      },
      [isSaving],
    )

    const handleOpenChange = useCallback(
      (nextOpen: boolean) => {
        if (isSaving) {
          return
        }

        onOpenChange(nextOpen)

        if (!nextOpen) {
          setSelectedTagIds(null)
          setIsClearSelected(false)
        }
      },
      [isSaving, onOpenChange],
    )

    const handleCancel = useCallback(() => {
      if (isSaving) {
        return
      }

      setSelectedTagIds(null)
      setIsClearSelected(false)
      onOpenChange(false)
    }, [isSaving, onOpenChange])

    const handleClear = useCallback(() => {
      if (isSaving) {
        return
      }

      setSelectedTagIds([])
      setIsClearSelected(true)
    }, [isSaving])

    const handleSave = useCallback(async () => {
      const nextTagIds = selectedTagIds

      if (
        nextTagIds === null ||
        (nextTagIds.length === 0 && !isClearSelected)
      ) {
        return
      }

      setIsSaving(true)
      try {
        await onChange(isClearSelected ? null : nextTagIds)
        setSelectedTagIds(null)
        setIsClearSelected(false)
        onOpenChange(false)
      } catch {
        // errors are handled by the mutation hook
      } finally {
        setIsSaving(false)
      }
    }, [isClearSelected, selectedTagIds, onChange, onOpenChange])

    const handleUndoClear = useCallback(() => {
      setSelectedTagIds(null)
      setIsClearSelected(false)
    }, [setSelectedTagIds, setIsClearSelected])

    const clearActive = isClearSelected
    const clearDisabled = !!disabled || isSaving
    const saveDisabled =
      selectedTagIds === null ||
      isSaving ||
      (selectedTagIds.length === 0 && !isClearSelected)
    const value = selectedTagIds ?? emptyTagIds

    return (
      <span ref={ref} {...rest} className="inline-flex">
        <Combobox
          open={disabled ? false : open}
          onOpenChange={disabled ? undefined : handleOpenChange}
        >
          <ComboboxTrigger asChild={true}>
            <Button
              variant="outline-compact"
              className="flex items-center active:scale-100"
              disabled={disabled}
            >
              <HiMiniPlus
                size={16}
                className="text-primary-700 dark:text-primary-300"
              />
              <span className="text-sm font-medium">{label}</span>
            </Button>
          </ComboboxTrigger>
          {open && !disabled && (
            <TagComboboxContent
              multiple={true}
              includeClear={true}
              value={value}
              onChange={handleSelectionChange}
              comboboxContentProps={{ align: 'end' }}
              tagType={tagType}
              footerActions={{
                clearActive,
                onCancel: handleCancel,
                onClear: handleClear,
                onUndoClear: handleUndoClear,
                onSave: handleSave,
                clearDisabled,
                clearHint:
                  'All tags will be removed from the selected items when you save.',
                saveDisabled,
                saveLoading: isSaving,
              }}
            />
          )}
        </Combobox>
      </span>
    )
  },
)

BulkTagCombobox.displayName = 'BulkTagCombobox'

interface TableContentProps {
  children: React.ReactNode
  className?: string
}

export const DataTableViewport: React.FC<TableContentProps> = ({
  children,
  className,
}) => {
  return (
    <div
      className={twMerge('relative flex-1 min-h-0 flex flex-col', className)}
    >
      {children}
    </div>
  )
}

export const DataTableContent = forwardRef<HTMLDivElement, TableContentProps>(
  ({ children, className }, ref) => {
    return (
      <div
        className={twMerge(
          'flex-1 min-h-0 flex flex-col w-full overflow-auto',
          className,
        )}
        ref={ref}
      >
        {children}
      </div>
    )
  },
)

DataTableContent.displayName = 'LibraryV2.Ui.Table.DataTableContent'

interface DataTableRootProps {
  children: React.ReactNode
  className?: string
}

export const DataTableRoot: React.FC<DataTableRootProps> = ({
  children,
  className,
}) => {
  return (
    <div
      className={twMerge(
        'h-full w-full flex flex-col overflow-hidden',
        className,
      )}
    >
      {children}
    </div>
  )
}

interface DataTableClearSelectionButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  onClick: () => void
  className?: string
}

export const DataTableClearSelectionButton = forwardRef<
  HTMLButtonElement,
  DataTableClearSelectionButtonProps
>(({ onClick, className, ...props }, ref) => {
  return (
    <button
      ref={ref}
      className={twMerge(
        'whitespace-nowrap text-sm font-medium text-blue-600 dark:text-blue-400 hover:opacity-70',
        className,
      )}
      onClick={onClick}
      {...props}
    >
      Deselect All
    </button>
  )
})

DataTableClearSelectionButton.displayName = 'DataTableClearSelectionButton'

interface DataTableDeleteButtonProps extends Omit<
  React.ComponentProps<typeof Button>,
  'variant' | 'children'
> {
  deleteButtonVariant?: Variant
  deleteButtonProps?: Omit<
    React.ComponentProps<typeof Button>,
    'variant' | 'onClick' | 'children'
  >
  deleteLabel?: string
}

export const DataTableDeleteButton = forwardRef<
  HTMLButtonElement,
  DataTableDeleteButtonProps
>(
  (
    {
      deleteButtonVariant = 'danger-compact',
      deleteButtonProps,
      deleteLabel = 'Delete',
      onClick,
      disabled,
      ...rest
    },
    ref,
  ) => {
    // Merge the disabled state from props and deleteButtonProps
    const isDisabled = disabled || deleteButtonProps?.disabled
    return (
      <Button
        ref={ref}
        variant={deleteButtonVariant}
        onClick={onClick}
        {...deleteButtonProps}
        {...rest}
        disabled={isDisabled}
      >
        <HiOutlineTrash size={16} />
        {deleteLabel}
      </Button>
    )
  },
)

DataTableDeleteButton.displayName = 'DataTableDeleteButton'

interface DataTableMergeButtonProps extends Omit<
  React.ComponentProps<typeof Button>,
  'variant' | 'children'
> {
  mergeLabel?: string
}

export const DataTableMergeButton = forwardRef<
  HTMLButtonElement,
  DataTableMergeButtonProps
>(({ onClick, mergeLabel = 'Merge', disabled, ...rest }, ref) => {
  return (
    <Button
      ref={ref}
      variant="blue-compact"
      onClick={onClick}
      disabled={disabled}
      className="flex items-center gap-2"
      {...rest}
    >
      <LuMerge size={16} />
      <span>{mergeLabel}</span>
    </Button>
  )
})

DataTableMergeButton.displayName = 'DataTableMergeButton'

// region: selection helpers
export const getRowRange = (
  rows: Row<any>[],
  idA: string,
  idB: string,
): Row<any>[] => {
  const range: Row<any>[] = []
  let foundStart = false
  let foundEnd = false
  for (let i = 0; i < rows.length; i += 1) {
    const r = rows[i]
    if (r.id === idA || r.id === idB) {
      if (foundStart) foundEnd = true
      if (!foundStart) foundStart = true
    }
    if (foundStart) range.push(r)
    if (foundEnd) break
  }
  if (!foundEnd) throw new Error('Could not find whole row range')
  return range
}

interface UseTableSelectionClickHandlerProps {
  row: Row<any>
  table: Table<any>
  lastSelectedRowId: string | null
  setLastSelectedRowId: (id: string | null) => void
}

export const useTableSelectionClickHandler = ({
  row,
  table,
  lastSelectedRowId,
  setLastSelectedRowId,
}: UseTableSelectionClickHandlerProps) => {
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation()
      const current = table.getState().rowSelection
      const last = lastSelectedRowId

      if (e.shiftKey && last && last !== row.id) {
        try {
          const { rows } = table.getRowModel()
          const rowsInRange = getRowRange(rows, row.id, last)
          const targetState = !current?.[row.id]
          const next = { ...current }
          for (const r of rowsInRange) next[r.id] = targetState
          table.setRowSelection(next)
        } catch {
          table.setRowSelection({
            ...current,
            [row.id]: !current?.[row.id],
          })
        }
      } else {
        table.setRowSelection({
          ...current,
          [row.id]: !current?.[row.id],
        })
      }

      setLastSelectedRowId(row.id)
    },
    [table, lastSelectedRowId, setLastSelectedRowId, row.id],
  )

  return handleClick
}

// endregion: selection helpers

// region: cell components

interface ShiftSelectCellProps {
  row: Row<any>
  table: Table<any>
  cell: Cell<any, any>
  lastSelectedRowId: string | null
  setLastSelectedRowId: (id: string | null) => void
  triggerClassName?: string
}

export const ShiftSelectCell: React.FC<ShiftSelectCellProps> = ({
  row,
  table,
  cell,
  lastSelectedRowId,
  setLastSelectedRowId,
  triggerClassName,
}) => {
  const handleClickDiv = useTableSelectionClickHandler({
    table,
    row,
    lastSelectedRowId,
    setLastSelectedRowId,
  })

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      handleClickDiv(e as unknown as React.MouseEvent<HTMLDivElement>)
    },
    [handleClickDiv],
  )

  const minSize = cell.column.columnDef.minSize
  const maxSize = cell.column.columnDef.maxSize
  const canSelect = row.getCanSelect()

  return (
    <div
      className="flex h-full items-center justify-center"
      style={{ minWidth: minSize, maxWidth: maxSize }}
    >
      <button
        type="button"
        onClick={handleClick}
        disabled={!canSelect}
        className={twMerge(
          'flex items-center justify-center w-11 h-11 rounded-full transition-colors duration-50',
          canSelect
            ? 'hover:bg-primary-200 dark:hover:bg-primary-700/70 cursor-pointer'
            : 'cursor-not-allowed opacity-50',
          triggerClassName,
        )}
        aria-label={`Select row ${row.id}`}
      >
        <Checkbox
          id={`${row.id}-select`}
          checked={row.getIsSelected()}
          disabled={!canSelect}
          childrenOnly={true}
          readOnly={true}
        />
      </button>
    </div>
  )
}

export const SelectHeaderCell: React.FC<HeaderContext<any, unknown>> = ({
  table,
  header,
}) => {
  const minSize = header.column.columnDef.minSize
  const maxSize = header.column.columnDef.maxSize
  const hasSelectableRows = table
    .getRowModel()
    .rows.some((row) => row.getCanSelect())
  return (
    <span
      className={twMerge('flex items-center justify-center pl-0')}
      style={{
        minWidth: minSize,
        maxWidth: maxSize,
      }}
    >
      <Checkbox
        id="select-all"
        checked={table.getIsAllRowsSelected()}
        onChange={table.getToggleAllRowsSelectedHandler()}
        disabled={!hasSelectableRows}
        childrenOnly={true}
        className="bg-white"
      />
    </span>
  )
}

export const SelectCell: React.FC<CellContext<any, unknown>> = ({
  row,
  table,
  cell,
}) => {
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      const current = table.getState().rowSelection
      table.setRowSelection({
        ...current,
        [row.id]: !current?.[row.id],
      })
    },
    [row, table],
  )
  const minSize = cell.column.columnDef.minSize
  const maxSize = cell.column.columnDef.maxSize
  const canSelect = row.getCanSelect()

  return (
    <div
      className="flex h-full items-center justify-center"
      style={{
        minWidth: minSize,
        maxWidth: maxSize,
      }}
    >
      <button
        type="button"
        onClick={handleClick}
        disabled={!canSelect}
        className={twMerge(
          'flex items-center justify-center w-9 h-9 rounded-full transition-colors',
          canSelect
            ? 'hover:bg-primary-200 dark:hover:bg-primary-700/70 cursor-pointer'
            : 'cursor-not-allowed opacity-50',
        )}
        aria-label={`Select row ${row.id}`}
      >
        <Checkbox
          id={`${row.id}-select`}
          checked={row.getIsSelected()}
          disabled={!canSelect}
          childrenOnly={true}
          readOnly={true}
        />
      </button>
    </div>
  )
}

interface DataCellLayoutProps {
  children: React.ReactNode
  className?: string
}

export const DataCellLayout: React.FC<DataCellLayoutProps> = ({
  children,
  className,
}) => {
  return <div className={twMerge('py-3 pr-3', className)}>{children}</div>
}

// endregion: cell components

// region: filters and filter components

export { createTagsFilter, type TagsFilterValue } from './filters'

interface UntaggedFilterChipProps {
  untaggedOnly: boolean
  onRemove: () => void
}

/**
 * Unified component for displaying the "Untagged" filter chip.
 * Uses useCallback to memoize the click handler.
 */
export const UntaggedFilterChip: React.FC<UntaggedFilterChipProps> = ({
  untaggedOnly,
  onRemove,
}) => {
  const handleClick = useCallback(
    (_value: string) => {
      onRemove()
    },
    [onRemove],
  )

  if (!untaggedOnly) {
    return null
  }

  return (
    <DataTableActiveFilterChips
      value={[{ label: 'Untagged', value: 'untagged' }]}
      onClick={handleClick}
    />
  )
}

interface TagFilterDropdownProps {
  tags: UUID[]
  untaggedOnly: boolean
  setFilters: (filters: { tags?: UUID[]; untagged_only?: boolean }) => void
  setPagination: (pagination: { pageIndex: number; pageSize: number }) => void
  setSelectedIds: (ids: UUID[]) => void
  pageSize: number
  label?: string
  showUntagged?: boolean
  tagClassName?: string
  /** Type of tags to filter. Defaults to 'organization' for backwards compatibility. */
  tagType?: TagType
}

/**
 * Unified tag filter dropdown component that handles all tag filtering logic.
 * Manages dropdown state, toggle callbacks, and renders the dropdown UI.
 */
export const TagFilterDropdown: React.FC<TagFilterDropdownProps> = ({
  tags,
  untaggedOnly,
  setFilters,
  setPagination,
  setSelectedIds,
  pageSize,
  label = 'Filter by Tag',
  showUntagged = true,
  tagClassName,
  tagType = 'organization',
}) => {
  const [tagDropdownOpen, setTagDropdownOpen] = useState<boolean>(false)

  const toggleTagFilter = useCallback(
    (tagId: UUID) => {
      const next = tags.includes(tagId)
        ? tags.filter((t) => t !== tagId)
        : [...tags, tagId]
      setFilters({ tags: next })
      setPagination({ pageIndex: 0, pageSize })
      setSelectedIds([])
    },
    [tags, pageSize, setFilters, setPagination, setSelectedIds],
  )

  const toggleUntaggedFilter = useCallback(() => {
    setFilters({ untagged_only: !untaggedOnly })
    setPagination({ pageIndex: 0, pageSize })
    setSelectedIds([])
  }, [untaggedOnly, pageSize, setFilters, setPagination, setSelectedIds])

  const badgeCount = tags.length + (untaggedOnly ? 1 : 0)

  return (
    <DropdownMenu open={tagDropdownOpen} onOpenChange={setTagDropdownOpen}>
      <DropdownMenuTrigger asChild={true}>
        <Button
          variant="outline-compact"
          className="flex items-center active:scale-100"
        >
          <span className="text-sm font-medium mr-1">{label}</span>
          {badgeCount > 0 && (
            <MiniBadge
              count={badgeCount}
              variant="soft"
              className="ml-[3px] text-[9px]"
            />
          )}
          <HiMiniChevronDown
            size={14}
            className="flex-shrink-0 text-primary-900 dark:text-primary-100"
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-60 max-h-[400px] overflow-y-auto"
        onCloseAutoFocus={handleCloseAutoFocus}
        autoFocus={false}
      >
        <TagDropdownMenuSubContent
          selectedTagIds={tags}
          tagClassName={twMerge('text-sm', tagClassName)}
          onSelect={toggleTagFilter}
          showUntagged={showUntagged}
          untaggedOnly={untaggedOnly}
          onToggleUntagged={toggleUntaggedFilter}
          tagType={tagType}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface TagFilterChipsProps {
  tags: UUID[]
  untaggedOnly: boolean
  selectedTagNames?: SelectedFilter[]
  onRemoveTag: (tagId: string) => void
  onRemoveUntagged: () => void
}

/**
 * Unified component for displaying active tag filter chips.
 * Shows both untagged chip and tag chips when filters are applied.
 */
export const TagFilterChips: React.FC<TagFilterChipsProps> = ({
  tags,
  untaggedOnly,
  selectedTagNames,
  onRemoveTag,
  onRemoveUntagged,
}) => {
  const handleRemoveTag = useCallback(
    (value: string) => {
      onRemoveTag(value)
    },
    [onRemoveTag],
  )

  const handleRemoveUntagged = useCallback(() => {
    onRemoveUntagged()
  }, [onRemoveUntagged])

  if (tags.length === 0 && !untaggedOnly) {
    return null
  }

  return (
    <>
      <UntaggedFilterChip
        untaggedOnly={untaggedOnly}
        onRemove={handleRemoveUntagged}
      />
      {selectedTagNames && selectedTagNames.length > 0 && (
        <DataTableActiveFilterChips
          value={selectedTagNames}
          onClick={handleRemoveTag}
        />
      )}
    </>
  )
}

// endregion: filters and filter components

// region: exports for other components

export {
  DataTableFacetDropdown,
  type TableFilterOption,
} from './DataTableFacetDropdown'

export {
  DataTableFilterMenu,
  type FilterGroup,
  type FilterOption,
} from './DataTableFilterMenu'

// endregion: exports for other components
